<template>
  <div class="page">
    <header class="top">
      <router-link to="/" class="wordmark">WHY Technologies</router-link>
      <div class="topnav" role="navigation" aria-label="Primary">
        <router-link to="/products" class="current">Work</router-link>
        <router-link to="/about">About</router-link>
      </div>
    </header>

    <main class="content">
      <section class="intro">
        <h1>We build and run the systems that professional-services firms operate on.</h1>
        <p>
          Our work is long-term. We design the platform, keep it running, and keep
          building on it as the business changes.
        </p>
      </section>

      <section class="block">
        <h2>Operations platforms</h2>
        <p>
          Case and client management systems built around a firm's actual
          process. Records, history, documents, and the relationships between
          them, with access controlled by role and by firm.
        </p>
      </section>

      <section class="block">
        <h2>Client and partner portals</h2>
        <p>
          Secure portals where clients upload documents, track their matters,
          and pay invoices, and where referral partners submit and follow new
          business. All of it tied directly to the platform's records.
        </p>
      </section>

      <section class="block">
        <h2>Workflow automation</h2>
        <p>
          Scheduled and on-demand automations that read and update records,
          prepare documents and correspondence, and route work to staff for
          approval before anything reaches a client. Every action is logged.
        </p>
      </section>

      <section class="block">
        <h2>Integrations</h2>
        <p>
          Telephony, payments, email, document generation, and external systems
          connected to the platform so staff work in one place.
        </p>
      </section>

      <section class="block how">
        <h2>How we work</h2>
        <dl>
          <div>
            <dt>Build</dt>
            <dd>We start from how the business runs today and build the platform to match it.</dd>
          </div>
          <div>
            <dt>Operate</dt>
            <dd>We host, monitor, back up, and maintain the systems we build.</dd>
          </div>
          <div>
            <dt>Extend</dt>
            <dd>New firms, new workflows, and new integrations are added on the same platform.</dd>
          </div>
        </dl>
      </section>

      <section class="block contact">
        <p>
          To talk about your operation, write to
          <a href="mailto:sae@whyt.io">sae@whyt.io</a>.
        </p>
      </section>
    </main>

    <footer class="footer">
      <span>WHY Technologies Inc.</span>
      <span>Bethesda, Maryland</span>
    </footer>
  </div>
</template>

<script>
export default {
  name: "ProductsView",
};
</script>

<style scoped>
.page {
  --ink: #1e2126;
  --muted: #5c6066;
  --rule: #e6e7e9;
  --accent: #1f3a5f;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  color: var(--ink);
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.top {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 32px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--rule);
}

.wordmark {
  color: var(--ink);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: -0.01em;
}

.wordmark:hover,
.wordmark:focus-visible {
  color: var(--accent);
  outline: none;
}

.topnav {
  display: flex;
  align-items: center;
  gap: 28px;
  width: auto;
  position: static;
}

.topnav a {
  color: var(--muted);
  text-decoration: none;
  font-size: 0.95rem;
  padding-bottom: 3px;
  border-bottom: 1px solid transparent;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.topnav a:hover,
.topnav a:focus-visible {
  color: var(--ink);
  outline: none;
}

.topnav a.current {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.content {
  flex: 1;
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  padding: 80px 24px 96px;
}

.intro h1 {
  font-size: clamp(1.75rem, 3.2vw, 2.5rem);
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0 0 24px;
}

.intro p {
  font-size: 1.125rem;
  line-height: 1.65;
  color: var(--muted);
  margin: 0;
}

.block {
  margin-top: 56px;
  padding-top: 28px;
  border-top: 1px solid var(--rule);
}

.block h2 {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--muted);
  margin: 0 0 14px;
}

.block p {
  font-size: 1.0625rem;
  line-height: 1.7;
  margin: 0;
}

.how dl {
  margin: 0;
  display: grid;
  gap: 20px;
}

.how dt {
  font-weight: 500;
  margin-bottom: 4px;
}

.how dd {
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
}

.contact p {
  color: var(--muted);
}

.contact a,
.footer a {
  color: var(--accent);
  text-decoration: none;
}

.contact a:hover,
.contact a:focus-visible,
.footer a:hover,
.footer a:focus-visible {
  text-decoration: underline;
  outline: none;
}

.footer {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 28px 24px;
  border-top: 1px solid var(--rule);
  font-size: 0.875rem;
  color: var(--muted);
}

@media (max-width: 600px) {
  .top {
    padding: 16px 20px;
  }
  .topnav {
    gap: 20px;
  }
  .content {
    padding: 56px 20px 72px;
  }
  .block {
    margin-top: 44px;
  }
  .footer {
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
}
</style>